<template>
  <v-dialog
    :value="value"
    max-width="460"
    overlay-color="black"
    overlay-opacity="0.75"
    content-class="plan-celebration-content"
    @input="$emit('input', $event)"
  >
    <v-card dark class="celebration-card">
      <div class="celebration-hero">
        <div
          class="celebration-badge"
          :class="{ 'celebration-badge--change': !isActivation }"
        >
          <v-icon size="34" :color="isActivation ? '#5ce5a8' : '#4395a3'">
            {{ isActivation ? "mdi-check-decagram" : "mdi-swap-horizontal-circle-outline" }}
          </v-icon>
        </div>
        <h2 class="celebration-title">{{ title }}</h2>
        <p class="celebration-text">{{ message }}</p>
      </div>

      <v-card-text class="celebration-note">
        <v-icon size="15" color="#8a93a3" class="mr-1">
          mdi-information-outline
        </v-icon>
        You can review or manage your subscription anytime from the Account
        panel.
      </v-card-text>

      <v-card-actions class="celebration-actions">
        <v-btn
          block
          large
          depressed
          color="primary"
          class="celebration-cta"
          @click="$emit('input', false)"
        >
          {{ isActivation ? "Awesome, let's go" : "Got it" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Celebration / confirmation modal for subscription lifecycle moments:
 *  - mode "activated": thank-you shown when a paid plan is activated
 *    (return from Stripe checkout or free → paid tier transition)
 *  - mode "changed": confirmation shown when the plan tier changes
 *    (paid → paid switch or paid → free downgrade)
 */
export default {
  name: "PlanCelebrationDialog",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "activated",
      validator: v => ["activated", "changed"].includes(v)
    },
    planLabel: {
      type: String,
      default: ""
    },
    previousPlanLabel: {
      type: String,
      default: ""
    }
  },
  computed: {
    isActivation() {
      return this.mode === "activated";
    },
    title() {
      return this.isActivation
        ? "Thank you for subscribing!"
        : "Plan change confirmed";
    },
    message() {
      if (this.isActivation) {
        if (this.planLabel) {
          return `Your ${this.planLabel} plan is now active. It may take a moment for all features to unlock.`;
        }
        return "Your subscription is being activated. It may take a moment for all features to unlock.";
      }
      if (this.planLabel && this.previousPlanLabel) {
        return `You switched from the ${this.previousPlanLabel} plan to the ${this.planLabel} plan.`;
      }
      if (this.planLabel) {
        return `Your plan is now ${this.planLabel}.`;
      }
      return "Your subscription plan has been updated.";
    }
  }
};
</script>

<style scoped>
.celebration-card {
  background: linear-gradient(165deg, #1e1e1e, #171717 58%) !important;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px !important;
  overflow: hidden;
}

.celebration-hero {
  padding: 32px 28px 0;
  text-align: center;
  background: radial-gradient(
    ellipse at top,
    rgba(92, 229, 168, 0.1),
    transparent 65%
  );
}

.celebration-badge {
  align-items: center;
  background: rgba(92, 229, 168, 0.1);
  border: 1px solid rgba(92, 229, 168, 0.35);
  border-radius: 50%;
  display: inline-flex;
  height: 66px;
  justify-content: center;
  margin-bottom: 16px;
  width: 66px;
}

.celebration-badge--change {
  background: rgba(67, 149, 163, 0.12);
  border-color: rgba(67, 149, 163, 0.4);
}

.celebration-title {
  color: #f5f7fb;
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 10px;
}

.celebration-text {
  color: #b5bcc7;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;
}

.celebration-note {
  align-items: center;
  color: #8a93a3 !important;
  display: flex;
  font-size: 12px !important;
  justify-content: center;
  padding: 16px 28px 0 !important;
}

.celebration-actions {
  padding: 16px 28px 26px !important;
}

.celebration-cta {
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
}
</style>

<style>
.plan-celebration-content {
  border-radius: 20px;
}
</style>
